/**
 * Deterministic content-binding layer for brand skinning.
 *
 * Maps the extracted client profile's real content — tagline, hero copy, CTA,
 * services, testimonials, about snippet — into the template's CodeStitch
 * slots (cs-topper/cs-title/cs-text/cs-item/cs-h3/cs-name...). This is the
 * layer that turns "brand shell swap" into actual composition.
 *
 * Rules:
 *  - capacity is min(client items, template cards): cards are NEVER added or
 *    removed; surplus template cards keep their (identity-scrubbed) copy and
 *    get data-pb-needs-review flags;
 *  - empty client values never blank a slot (reported as skipped instead);
 *  - every bound slot is stamped data-pb-slot="<category>.<key>.<i>" — the
 *    join key for the wizard's preview diff, idempotent resync, and the
 *    optional LLM length-fitting pass;
 *  - binding a slot whose text already equals the client value is a no-op,
 *    so re-running produces no diff.
 *
 * Worker-safe: pure DOM-element functions.
 */

import { reportPush } from './skin-report.js';

const HERO_CATEGORIES = new Set(['hero']);
const REVIEW_CATEGORIES = new Set(['reviews', 'testimonials']);
const SERVICE_CATEGORIES = new Set(['services', 'service']);
const ABOUT_CATEGORIES = new Set(['about']);
const CTA_CATEGORIES = new Set(['cta', 'call-to-action', 'banner']);

function clean(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function textOf(el) {
  return clean(el?.textContent || '');
}

function bindSlot(el, value, slotId, report, bucketKey) {
  if (!el || !clean(value)) return false;
  if (textOf(el) === clean(value)) {
    el.setAttribute('data-pb-slot', slotId);
    return false; // idempotent re-run — already bound
  }
  el.textContent = clean(value);
  el.setAttribute('data-pb-slot', slotId);
  if (report && bucketKey === 'copy') {
    const key = slotId.split('.').slice(0, 2).join('.');
    if (!report.applied.bindings.copy.includes(key)) report.applied.bindings.copy.push(key);
  } else if (report && bucketKey) {
    report.applied.bindings[bucketKey] += 1;
  }
  return true;
}

function firstIn(root, selectors) {
  for (const selector of selectors) {
    const el = root.querySelector?.(selector);
    if (el) return el;
  }
  return null;
}

function bindHero(root, clientProfile, report) {
  const copy = clientProfile.copy || {};
  const topper = root.querySelector?.('.cs-topper');
  if (copy.tagline) {
    bindSlot(topper, copy.tagline, 'hero.tagline.0', report, 'copy');
  } else if (topper) {
    reportPush(report, 'skipped', { field: 'tagline', reason: 'none extracted' });
  }

  const text = root.querySelector?.('.cs-text');
  if (copy.heroText) {
    bindSlot(text, copy.heroText, 'hero.heroText.0', report, 'copy');
  } else if (text) {
    reportPush(report, 'skipped', { field: 'heroText', reason: 'none extracted' });
  }

  const cta = root.querySelector?.('.cs-button-solid');
  if (copy.ctaText) {
    bindSlot(cta, copy.ctaText, 'hero.ctaText.0', report, 'copy');
  } else if (cta) {
    reportPush(report, 'skipped', { field: 'ctaText', reason: 'none extracted' });
  }
}

function reviewCards(root) {
  const items = Array.from(root.querySelectorAll?.('.cs-item') || []);
  if (items.length) return items;
  return Array.from(root.querySelectorAll?.('.cs-review') || []).map(
    (el) => el.closest?.('li, article, div') || el
  );
}

function bindReviews(root, clientProfile, report, sectionId) {
  const testimonials = Array.isArray(clientProfile.testimonials) ? clientProfile.testimonials : [];
  const cards = reviewCards(root);
  if (!cards.length) return;
  if (!testimonials.length) {
    reportPush(report, 'skipped', { field: 'testimonials', reason: 'none extracted' });
    return;
  }

  const bound = Math.min(cards.length, testimonials.length);
  for (let i = 0; i < bound; i++) {
    const card = cards[i];
    const quoteEl = firstIn(card, ['.cs-item-text', '.cs-review', '.cs-text', 'p']);
    if (bindSlot(quoteEl, testimonials[i].quote, `reviews.quote.${i}`, report, null) && report) {
      report.applied.bindings.testimonials += 1;
    }
    // SU-6. An author may now legitimately be EMPTY: the profiler emits a
    // testimonial without one rather than inventing `'Anonymous'`, which it
    // used to do on 96% of extracted reviews. `bindSlot` skips an empty value,
    // and skipping here would leave the TEMPLATE's author name in place — a
    // real person from another business, named on this client's site. So an
    // unattributed quote clears the name and says so.
    const authorEl = card.querySelector?.('.cs-name');
    const author = testimonials[i].author;
    if (author) {
      bindSlot(authorEl, author, `reviews.author.${i}`, report, null);
    } else if (authorEl) {
      authorEl.textContent = '';
      authorEl.setAttribute('data-pb-needs-review', 'testimonial-author');
      reportPush(report, 'needsReview', {
        kind: 'testimonial',
        sectionId,
        detail: 'client review had no attributable name — template name cleared'
      });
    }
  }
  for (let i = bound; i < cards.length; i++) {
    cards[i].setAttribute('data-pb-needs-review', 'testimonial');
    reportPush(report, 'needsReview', {
      kind: 'testimonial',
      sectionId,
      detail: 'template testimonial kept — client had fewer'
    });
  }
}

function serviceCards(root) {
  const grouped = Array.from(root.querySelectorAll?.('.cs-card-group .cs-item') || []);
  if (grouped.length) return grouped;
  return Array.from(root.querySelectorAll?.('.cs-item') || []);
}

function bindServices(root, clientProfile, report, sectionId) {
  const services = (Array.isArray(clientProfile.services) ? clientProfile.services : [])
    .map(clean)
    .filter(Boolean);
  const cards = serviceCards(root);
  if (!cards.length) return;
  if (!services.length) {
    reportPush(report, 'skipped', { field: 'services', reason: 'none extracted' });
    return;
  }

  const bound = Math.min(cards.length, services.length);
  for (let i = 0; i < bound; i++) {
    const heading = firstIn(cards[i], ['.cs-h3', 'h3', '.cs-title', 'h2']);
    if (bindSlot(heading, services[i], `services.name.${i}`, report, null) && report) {
      report.applied.bindings.services += 1;
    }
  }
  // SK-8. The surplus loop `bindReviews` has had all along and this did not.
  //
  // A template with six service cards and a client with four left TWO cards
  // still advertising the source business's services — silently, with nothing
  // in the report and no marker on the card. That is the same defect
  // `bindReviews` already handles for testimonials, and there was no reason for
  // the two to differ.
  for (let i = bound; i < cards.length; i++) {
    cards[i].setAttribute('data-pb-needs-review', 'service');
    reportPush(report, 'needsReview', {
      kind: 'service',
      sectionId,
      detail: 'template service kept — the client listed fewer'
    });
  }
}

/**
 * SK-8. A call-to-action's topper and button labels, and NEVER its `.cs-title`.
 *
 * The title of a CTA band is the offer — "Ready to transform your kitchen?" —
 * and the client profile has nothing that means the same thing. Overwriting it
 * with a tagline produces a headline that does not follow from the button
 * beneath it. The topper (the small line above) and the button label are
 * different: those are short, functional, and the profile really does carry
 * their counterparts.
 */
function bindCta(root, clientProfile, report) {
  const copy = clientProfile.copy || {};
  const topper = root.querySelector?.('.cs-topper');
  if (copy.tagline) {
    bindSlot(topper, copy.tagline, 'cta.tagline.0', report, 'copy');
  } else if (topper) {
    reportPush(report, 'skipped', { field: 'tagline', reason: 'none extracted' });
  }

  if (!copy.ctaText) return;
  const buttons = Array.from(root.querySelectorAll?.('.cs-button-solid, .cs-button') || []);
  buttons.forEach((button, index) => {
    // A button whose label is an address or a phone number is a link, not a
    // call to action; those are the link layer's business.
    const label = clean(button.textContent);
    if (!label || /\d{3}/.test(label)) return;
    bindSlot(button, copy.ctaText, `cta.ctaText.${index}`, report, 'copy');
  });
}

function bindAbout(root, clientProfile, report) {
  const snippet = clean(clientProfile.copy?.aboutSnippet);
  const text = root.querySelector?.('.cs-text');
  if (snippet) {
    bindSlot(text, snippet, 'about.aboutSnippet.0', report, 'copy');
  } else if (text) {
    reportPush(report, 'skipped', { field: 'aboutSnippet', reason: 'none extracted' });
  }
}

/**
 * Apply category-appropriate content bindings.
 *
 * @param {Element} root
 * @param {string} sectionCategory
 * @param {object} clientProfile
 * @param {object} [options]
 * @param {object|null} [report]
 * @param {string} [sectionId]
 */
export function applyContentBindings(
  root,
  sectionCategory,
  clientProfile,
  options = {},
  report = null,
  sectionId = ''
) {
  const category = clean(sectionCategory).toLowerCase();
  if (!root || !clientProfile) return;
  if (HERO_CATEGORIES.has(category)) bindHero(root, clientProfile, report);
  else if (REVIEW_CATEGORIES.has(category)) bindReviews(root, clientProfile, report, sectionId);
  else if (SERVICE_CATEGORIES.has(category)) bindServices(root, clientProfile, report, sectionId);
  else if (ABOUT_CATEGORIES.has(category)) bindAbout(root, clientProfile, report);
  else if (CTA_CATEGORIES.has(category)) bindCta(root, clientProfile, report);
}
